import React, { useEffect, useRef, useState } from 'react'
import Modal from 'blocks.simple/modal/modal'
import Icon from 'blocks.simple/icon/icon'
import Checkbox from 'blocks.simple/checkbox/checkbox'
import EditText from 'blocks.simple/editText/editText'
import { Button } from 'molecules/Button'
import translate from 'core/translate'
import { api } from 'core/api/ConnectionManager'
import { findDevicesById } from 'core/helpers/digitalSignages'
import helpers from 'core/helpers'
import { getWordTranslateWithDeclension } from 'core/utils/coreUtil'
import { cn } from 'ethcss'
import { grid, text } from 'theme'
import { Typography } from 'atoms/Typography'
import { scheduleDevicesModalStyles as styles } from './ScheduleDevicesModal-styles'

interface IScheduleDeviceName {
    id: number
    name: string
}

interface IScheduleDevicesResponse {
    data?: IScheduleDeviceName[]
    total?: number
    nextPageToken?: string | null
}

interface IScheduleDevicesModalProps {
    scheduleId: number
    total: number
    onClose: () => void
}

interface IDevicesPage {
    list: IScheduleDeviceName[]
    nextPageToken: string | null
    total?: number
}

const SEARCH_DEBOUNCE_MS = 400

const parseDevices = (res: IScheduleDevicesResponse | IScheduleDeviceName[]): IScheduleDeviceName[] => {
    if (Array.isArray(res)) return res
    return Array.isArray(res?.data) ? res.data : []
}

export const ScheduleDevicesModal = ({ scheduleId, total, onClose }: IScheduleDevicesModalProps) => {
    const [devices, setDevices] = useState<IScheduleDeviceName[]>([])
    const [nextPageToken, setNextPageToken] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [listTotal, setListTotal] = useState(total)
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [search, setSearch] = useState('')
    const [appliedSearch, setAppliedSearch] = useState('')
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const fetchPage = async (pageToken?: string | null, searchValue: string = appliedSearch): Promise<IDevicesPage> => {
        const params = pageToken
            ? { nextPageToken: pageToken }
            : {
                  scheduleId,
                  sort: 'name',
                  limit: 50,
                  ...(searchValue.trim() ? { search: searchValue.trim() } : {}),
              }

        const res: IScheduleDevicesResponse = await api.send<
            { scheduleId?: number; sort?: string; limit?: number; nextPageToken?: string; search?: string },
            IScheduleDevicesResponse
        >('getScheduleDevices', params)

        return {
            list: parseDevices(res),
            nextPageToken: res.nextPageToken || null,
            total: typeof res.total === 'number' ? res.total : undefined,
        }
    }

    const loadPage = async (pageToken?: string | null, searchValue: string = appliedSearch) => {
        if (loading) return

        setLoading(true)
        try {
            const page = await fetchPage(pageToken, searchValue)
            setDevices((current) => (pageToken ? [...current, ...page.list] : page.list))
            setNextPageToken(page.nextPageToken)
            if (page.total !== undefined) {
                setListTotal(page.total)
            } else if (!pageToken) {
                setListTotal(page.list.length)
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadPage(null, appliedSearch)
    }, [scheduleId, appliedSearch])

    useEffect(() => {
        helpers.disableWindowScroll(true, 'schedule-devices-modal')
        return () => {
            helpers.disableWindowScroll(false, 'schedule-devices-modal')
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current)
            }
        }
    }, [])

    const onSearchChange = (value: string) => {
        setSearch(value)
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current)
        }
        searchTimeoutRef.current = setTimeout(() => {
            setAppliedSearch(value)
            setSelectedIds([])
            setNextPageToken(null)
        }, SEARCH_DEBOUNCE_MS)
    }

    const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
        if (!nextPageToken || loading) return

        const { scrollHeight, clientHeight, scrollTop } = event.currentTarget
        if (scrollHeight - clientHeight - scrollTop < 40) {
            loadPage(nextPageToken)
        }
    }

    const isSelected = (id: number) => selectedIds.includes(id)

    const toggleDevice = (id: number) => {
        setSelectedIds((current) =>
            current.includes(id) ? current.filter((deviceId) => deviceId !== id) : [...current, id]
        )
    }

    const openSelectedDevices = () => {
        if (!selectedIds.length) return
        findDevicesById(selectedIds)
        onClose()
    }

    const devicesTitle = `${listTotal} ${getWordTranslateWithDeclension(listTotal, [
        translate('device'),
        translate('devices'),
        translate('devices|'),
    ]).toLowerCase()}`

    return (
        <Modal open={true} showCloseIcon={false} mod="clear" transitionName="fade">
            <div className={styles.modalWrapper}>
                <section className={styles.modalInner} role="dialog" aria-modal="true">
                    <header className={styles.modalHeader}>
                        <Typography type="title">{devicesTitle}</Typography>
                        <button
                            type="button"
                            className={styles.closeButton}
                            aria-label={translate('close')}
                            onClick={onClose}
                        >
                            <Icon type="close_tiny" size={16} />
                        </button>
                    </header>

                    <div className={styles.modalBody}>
                        <div className={styles.searchField}>
                            <EditText
                                icon="search"
                                mod="withBorder"
                                placeholder={translate('search')}
                                value={search}
                                onChange={onSearchChange}
                            />
                        </div>

                        <div className={styles.deviceList} onScroll={onScroll}>
                            {devices.map((device) => (
                                <div
                                    key={device.id}
                                    className={styles.deviceRow}
                                    onClick={() => toggleDevice(device.id)}
                                >
                                    <div
                                        className={styles.deviceRowCheckbox}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        <Checkbox
                                            checked={isSelected(device.id)}
                                            onClick={() => toggleDevice(device.id)}
                                        />
                                    </div>
                                    <div>
                                        <div>{device.name}</div>
                                        <div className={cn(text.mini)} style={{ opacity: 0.7 }}>
                                            ID: {device.id}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className={cn(grid.pt_mini, grid.pb_mini, grid.pl_mini)}>
                                    {translate('loading')}
                                </div>
                            )}
                            {!loading && devices.length === 0 && (
                                <div className={styles.emptyList}>{translate('notFound')}</div>
                            )}
                        </div>
                    </div>

                    <footer className={styles.footer}>
                        <Button
                            mod="fillBrand"
                            className={styles.actionButton}
                            disabled={!selectedIds.length}
                            onClick={openSelectedDevices}
                        >
                            {`${translate('goToDevices')} (${selectedIds.length})`}
                        </Button>
                    </footer>
                </section>
            </div>
        </Modal>
    )
}
